import React, { Suspense, useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useApp } from '@/store/AppContext';
import moduleRegistry from '@/config/moduleRegistry';
import ModeToggle from '@/components/shared/ModeToggle';
import Gallery from '@/components/shared/Gallery';
import WelcomeModal from '@/components/shared/WelcomeModal';
import GuestPublicConsentModal from '@/components/shared/GuestPublicConsentModal';
import SignIn from '@/pages/auth/SignIn';
import SignUp from '@/pages/auth/SignUp';
import SettingsPage from '@/pages/settings/SettingsPage';
import DashboardPage from '@/pages/dashboard/DashboardPage';
import StylesPage from '@/pages/styles/StylesPage';
import TemplatesPage from '@/pages/templates/TemplatesPage';
import OnboardingPage from '@/pages/onboarding/Onboarding';
import TrialBanner from '@/components/shared/TrialBanner';
import { PWAInstallPrompt } from '@/components/shared/PWAInstallPrompt';
import { useTranslation, setAppLanguage } from '@/i18n/useTranslation';
import { getAppPathname, getLocaleFromPathname, matchAppPath, withLocalePath } from '@/i18n/localeRouting';
import { useAppNavigate } from '@/hooks/useAppNavigate';
import { ImageStyle } from '@/types';
import {
  GUEST_CONSENT_REQUIRED_EVENT,
  hasGuestPublicConsent,
  setGuestPublicConsent,
} from '@/utils/guestConsent';

function App() {
  const { activeModule, setActiveModule, setMode, isAuthenticated, isGuestMode, enterGuestMode, exitGuestMode, setIsAuthenticated, user, userLoaded } = useApp();
  const location = useLocation();
  const navigate = useAppNavigate();
  const [searchParams] = useSearchParams();
  const { t, language } = useTranslation();
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showGuestConsentModal, setShowGuestConsentModal] = useState(false);
  const localeFromUrl = getLocaleFromPathname(location.pathname);
  const appPath = getAppPathname(location.pathname);
  
  const ActiveModuleComponent = moduleRegistry[activeModule]?.component;
  const isAuthRoute = matchAppPath(location.pathname, '/signin') || matchAppPath(location.pathname, '/signup');
  const isOnboardingRoute = matchAppPath(location.pathname, '/onboarding');
  const showGuestRegisterBanner = !isAuthenticated && isGuestMode && matchAppPath(location.pathname, '/home');

  useEffect(() => {
    if (localeFromUrl) {
      if (localeFromUrl !== language) {
        setAppLanguage(localeFromUrl);
      }
      return;
    }

    navigate(`${location.pathname}${location.search}${location.hash}`, { replace: true });
  }, [localeFromUrl, location.pathname, location.search, location.hash, navigate]);

  // Hotjar: report virtual page view on route change (SPA)
  useEffect(() => {
    const isHttps = window.location.protocol === 'https:';
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (!isHttps || isLocal) return;
    const path = location.pathname + location.search;
    const hj = (window as unknown as { hj?: (cmd: string, ...args: unknown[]) => void }).hj;
    if (typeof hj === 'function') {
      hj('stateChange', path);
    }
  }, [location.pathname, location.search]);

  // Sync URL with mode
  useEffect(() => {
    if (!localeFromUrl) return;

    if (appPath.startsWith('/create')) {
      setMode('create');
    } else if (appPath.startsWith('/browse')) {
      setMode('browse');
    } else if (appPath.startsWith('/templates')) {
      setMode('browse');
    } else if (appPath.startsWith('/styles')) {
      setMode('create');
    } else if (appPath.startsWith('/home')) {
      setMode('create');
    } else if (appPath.startsWith('/signin') || appPath.startsWith('/signup')) {
      setMode('create');
    } else if (appPath.startsWith('/onboarding')) {
      setMode('create');
    } else if (appPath.startsWith('/settings')) {
      setMode('create');
    } else if (appPath === '/') {
      navigate('/home', { replace: true });
    } else {
      const knownRoute =
        appPath.startsWith('/create') ||
        appPath.startsWith('/browse') ||
        appPath.startsWith('/templates') ||
        appPath.startsWith('/styles') ||
        appPath.startsWith('/home') ||
        appPath.startsWith('/signin') ||
        appPath.startsWith('/signup') ||
        appPath.startsWith('/onboarding') ||
        appPath.startsWith('/settings');

      if (!knownRoute) {
        navigate('/home', { replace: true });
      }
    }
  }, [appPath, localeFromUrl, setMode, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!userLoaded) return;

    const onboardingCompleted = user?.onboarding_completed;
    if (onboardingCompleted !== true && !isOnboardingRoute) {
      navigate('/onboarding', { replace: true });
      return;
    }

    if (onboardingCompleted === true && isOnboardingRoute) {
      navigate('/home', { replace: true });
    }
  }, [isAuthenticated, isOnboardingRoute, navigate, user?.onboarding_completed, userLoaded]);

  // Sync activeModule with URL imageStyle parameter
  useEffect(() => {
    if (!localeFromUrl) return;

    if (appPath.startsWith('/create')) {
      const imageStyle = searchParams.get('imageStyle');
      const targetStyle = imageStyle || null;
      if (targetStyle && ['photo', 'illustration', 'infographic', 'icon', 'packshot', 'mockup'].includes(targetStyle)) {
        if (activeModule !== targetStyle) {
          setActiveModule(targetStyle as ImageStyle);
        }
      }
    }
  }, [searchParams, appPath, localeFromUrl, activeModule, setActiveModule]);

  // Welcome modal visibility: show only for anonymous non-guest, and never on auth routes
  useEffect(() => {
    if (isAuthRoute) {
      setShowWelcomeModal(false);
      return;
    }
    if (!isAuthenticated && !isGuestMode) {
      setShowWelcomeModal(true);
    }
  }, [isAuthenticated, isGuestMode, isAuthRoute]);

  useEffect(() => {
    if (isAuthenticated) return;
    if (isGuestMode && !hasGuestPublicConsent()) {
      setShowGuestConsentModal(true);
    }
  }, [isAuthenticated, isGuestMode]);

  useEffect(() => {
    const handleConsentRequired = () => setShowGuestConsentModal(true);
    window.addEventListener(GUEST_CONSENT_REQUIRED_EVENT, handleConsentRequired);
    return () => window.removeEventListener(GUEST_CONSENT_REQUIRED_EVENT, handleConsentRequired);
  }, []);

  const beginGuestFlow = () => {
    setShowWelcomeModal(false);
    setShowGuestConsentModal(true);
  };

  const handleAcceptGuestConsent = () => {
    setGuestPublicConsent();
    if (!isGuestMode) {
      enterGuestMode();
    }
    setShowGuestConsentModal(false);
  };

  if (!localeFromUrl) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-text-secondary">{t('loading')}</div>
      </div>
    );
  }

  // 1) Auth routes: no sidebar, no header
  if (isAuthRoute) {
    return (
      <Suspense fallback={
        <div className="h-screen flex items-center justify-center">
          <div className="text-text-secondary">{t('loading')}</div>
        </div>
      }>
        <Routes>
          <Route
            path="/:locale/signin"
            element={
              <SignIn
                onAuthSuccess={() => {
                  setIsAuthenticated(true);
                  exitGuestMode();
                  setShowWelcomeModal(false);
                }}
              />
            }
          />
          <Route path="/:locale/signup" element={<SignUp />} />
        </Routes>
      </Suspense>
    );
  }

  // 2) Main app routes: full layout + optional welcome modal
  return (
    <>
      <WelcomeModal
        isOpen={showWelcomeModal}
        onClose={beginGuestFlow}
        onContinueFree={beginGuestFlow}
        onSignIn={() => {
          setShowWelcomeModal(false);
          navigate('/signin');
        }}
        onSignUp={() => {
          setShowWelcomeModal(false);
          navigate('/signup');
        }}
      />

      <GuestPublicConsentModal
        isOpen={showGuestConsentModal && !isAuthenticated}
        onAccept={handleAcceptGuestConsent}
        onSignUp={() => {
          setShowGuestConsentModal(false);
          navigate('/signup');
        }}
        onSignIn={() => {
          setShowGuestConsentModal(false);
          navigate('/signin');
        }}
      />

      <div className="h-screen flex overflow-hidden">
        <ModeToggle />

        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          <TrialBanner />

          {showGuestRegisterBanner && (
            <div className="relative px-3 md:px-4 py-2 border-b border-border bg-bg-panel/95 backdrop-blur-sm flex items-center justify-center">
            <p className="text-center text-xs md:text-sm text-text-secondary px-28 sm:px-36 md:px-44">
              {t('guest.banner.message')}
            </p>
              <div className="absolute right-3 md:right-4 inset-y-0 flex items-center gap-2">
                <button
                  onClick={() => navigate('/signin')}
                  className="h-8 px-3 rounded-full border border-border text-text-primary text-xs hover:bg-bg-control transition-all"
                >
                  {t('auth.signIn')}
                </button>
                <button
                  onClick={() => navigate('/signup')}
                  className="h-8 px-3 rounded-full bg-accent hover:bg-accent-hover text-bg-main font-semibold text-xs transition-all"
                >
                  {t('auth.registerFree')}
                </button>
              </div>
            </div>
          )}

          <main className={`flex-1 overflow-hidden pwa-content md:pt-0 md:pb-0 ${matchAppPath(location.pathname, '/browse') ? 'pwa-content-browse' : ''}`}>
            <Suspense fallback={
              <div className="h-full flex items-center justify-center">
                <div className="text-text-secondary">{t('loading')}</div>
              </div>
            }>
              <Routes>
                <Route path="/:locale/home" element={<DashboardPage />} />
                <Route path="/:locale/styles" element={<StylesPage />} />
                <Route path="/:locale/settings" element={<SettingsPage />} />
                <Route path="/:locale/create" element={ActiveModuleComponent ? <ActiveModuleComponent /> : null} />
                <Route path="/:locale/create/:sessionId" element={ActiveModuleComponent ? <ActiveModuleComponent /> : null} />
                <Route path="/:locale/browse" element={<Gallery />} />
                <Route path="/:locale/browse/:uuid" element={<Gallery />} />
                <Route path="/:locale/templates" element={<TemplatesPage />} />
                <Route
                  path="/:locale/onboarding"
                  element={isAuthenticated ? <OnboardingPage /> : <Navigate to={withLocalePath('/signin', localeFromUrl)} replace />}
                />
                <Route path="/:locale" element={<Navigate to={withLocalePath('/home', localeFromUrl)} replace />} />
              </Routes>
            </Suspense>
          </main>
        </div>
      </div>

      {/* PWA Install Prompt */}
      <PWAInstallPrompt />
    </>
  );
}

export default App;
